import { useState, useRef, useCallback, useEffect } from 'react';
import styles from './AddUsersBulk.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Tooltip } from '@dsi/react-eds';
import { DragAndDrop, Notification } from '@eds/vanilla';
import UploadResultsTable from './UploadResultsTable';
import { bulkUserService, BulkUserResult } from '../../../api/bulkUserService';

interface AddUsersBulkProps {
  onAdd: (users: { email: string; roles: string[]; role: string; id: string }[]) => void;
}

interface FileState {
  file: File;
  id: number;
  state: string;
}

interface UploadResult {
  email: string;
  role: string;
  password?: string;
  error: string;
}

type ProgressCallback = (progress: number) => void;
type CompleteCallback = () => void;
type ErrorCallback = () => void;

function AddUsersBulk({ onAdd }: AddUsersBulkProps) {
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);

  const dragAndDropRef = useRef<DragAndDrop | null>(null);
  const fileUploadMapRef = useRef<{ [key in number]: AbortController }>({});
  const acceptedFilesRef = useRef<Set<string>>(new Set());
  const onAddRef = useRef(onAdd);
  const callbacksRef = useRef<{
    onAbort: ((id: number) => void) | null;
    onBeforeSelect: ((file: File, state: FileState[]) => boolean) | null;
    onSelect: ((id: number, file: File, next: ProgressCallback, complete: CompleteCallback, error: ErrorCallback) => void) | null;
    onStateChange: ((state: FileState) => void) | null;
  }>({
    onAbort: null,
    onBeforeSelect: null,
    onSelect: null,
    onStateChange: null
  });

  // Keep onAdd ref up to date
  onAddRef.current = onAdd;

  const showNotification = useCallback((title: string, message: string, color: string) => {
    const notification = new Notification({
      title: title,
      stripeColor: color,
      description: message
    });
    notification.init();
  }, []);

  const uploadUsersWithProgress = useCallback(
    async (file: File, abortController: AbortController, next: ProgressCallback, complete: CompleteCallback, error: ErrorCallback) => {
      try {
        let progress = 20;
        next(progress);

        if (abortController.signal.aborted) return;

        try {
          const result: BulkUserResult = await bulkUserService.bulkCreateFromFile(file, true);

          progress = 100;
          next(progress);

          if (abortController.signal.aborted) return;

          // Process the API response - show successful users with passwords and failed users
          const successResults = result.successful.map((success) => ({
            email: success.user?.email || success.username,
            role: success.user?.roles?.[0] || 'user',
            password: success.user?.password || '',
            error: ''
          }));
          const failedResults = result.failed.map((failure) => ({
            email: failure.username || 'Unknown',
            role: 'N/A',
            password: '',
            error: failure.error
          }));

          setUploadResults([...successResults, ...failedResults]);

          // Set success message
          if (result.summary.successful > 0) {
            setUploadSuccess(`${result.summary.successful} users have been added successfully to the system`);

            // Show notification immediately with the count from the result
            showNotification('Upload completed', `${result.summary.successful} users have been added successfully to the system`, 'green');

            // Call onAdd with successfully created users
            const usersForCallback = result.successful.map((success) => ({
              email: success.user.email || success.username,
              roles: success.user.roles || ['user'],
              role: success.user.roles?.[0] || 'user',
              id: success.user.id
            }));
            onAddRef.current(usersForCallback);
          }

          complete();

          // Clean up the upload map and accepted files
          const keys = Object.keys(fileUploadMapRef.current);
          if (keys.length > 0) {
            delete fileUploadMapRef.current[parseInt(keys[0], 10)];
          }

          // Clear accepted files after successful upload
          setTimeout(() => {
            acceptedFilesRef.current.clear();
          }, 1000);
        } catch (apiError: unknown) {
          console.error('API Error:', apiError);
          const errorMessage =
            apiError instanceof Error
              ? apiError.message
              : (apiError as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
                (apiError as { message?: string })?.message ||
                'Failed to process file';

          const failureResult = [
            {
              email: file.name,
              role: 'N/A',
              error: errorMessage
            }
          ];
          setUploadResults(failureResult);
          error();
        }
      } catch (err: unknown) {
        console.error('Upload error:', err);
        const errorMessage = (err as Error)?.message || 'Upload failed. Please try again.';
        setUploadResults([{ email: file.name, role: 'N/A', error: errorMessage }]);
        error();
      }
    },
    [showNotification]
  );

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const onBeforeSelect = useCallback(
    (file: File, _state: FileState[]) => {
      // Check file type - accept Excel files only
      if (
        !(
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') ||
          file.name.endsWith('.xls')
        )
      ) {
        showNotification('Invalid file type', 'Please upload an Excel file (.xlsx or .xls)', 'red');
        return false;
      }

      // Create a unique key for this file
      const fileKey = `${file.name}_${file.size}_${file.lastModified}`;

      // Check if we've already accepted this exact file
      if (acceptedFilesRef.current.has(fileKey)) {
        return false;
      }

      // Add to accepted files
      acceptedFilesRef.current.add(fileKey);

      // Clear previous results only on first file
      if (acceptedFilesRef.current.size === 1) {
        setUploadSuccess('');
        setUploadResults([]);
      }

      return true;
    },
    [showNotification]
  );
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const onSelect = useCallback(
    (id: number, file: File, next: ProgressCallback, complete: CompleteCallback, error: ErrorCallback) => {
      // Check if this file is already being processed
      if (fileUploadMapRef.current[id]) {
        // Complete the duplicate request immediately to prevent stuck progress bar
        complete();
        return;
      }

      const abortController = new AbortController();
      fileUploadMapRef.current[id] = abortController;

      uploadUsersWithProgress(file, abortController, next, complete, error);
    },
    [uploadUsersWithProgress]
  );

  const onAbort = useCallback((id: number) => {
    if (fileUploadMapRef.current[id]) {
      fileUploadMapRef.current[id].abort();
      delete fileUploadMapRef.current[id];
    }
  }, []);

  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  const onStateChange = useCallback(
    (state: FileState) => {
      if (state.state === 'completed') {
        // Notification is now handled in uploadUsersWithProgress where we have the actual count

        const timeoutId = setTimeout(() => {
          const dadEl = document.querySelector('#BulkUserUploadDragAndDrop') as HTMLElement;
          if (dadEl) {
            const headerEl = dadEl.querySelector('.header-files');
            const contentEl = dadEl.querySelector('.content-files');
            if (headerEl) headerEl.classList.remove('hidden');
            if (contentEl) contentEl.classList.remove('hidden');
          }
          timeoutRefs.current.delete(timeoutId);
        }, 500);
        timeoutRefs.current.add(timeoutId);
      } else if (state.state === 'error') {
        showNotification('Upload failed', `File ${state.file.name} failed to upload`, 'red');

        const timeoutId = setTimeout(() => {
          const dadEl = document.querySelector('#BulkUserUploadDragAndDrop') as HTMLElement;
          if (dadEl) {
            const headerEl = dadEl.querySelector('.header-files');
            const contentEl = dadEl.querySelector('.content-files');
            if (headerEl) headerEl.classList.remove('hidden');
            if (contentEl) contentEl.classList.remove('hidden');
          }
          timeoutRefs.current.delete(timeoutId);
        }, 500);
        timeoutRefs.current.add(timeoutId);
      }
    },
    [showNotification]
  );

  // Initialize drag and drop only once on mount
  useEffect(() => {
    const dadEl = document.querySelector('#BulkUserUploadDragAndDrop') as HTMLElement;
    if (dadEl && !dragAndDropRef.current) {
      dragAndDropRef.current = new DragAndDrop({
        element: dadEl,
        onAbort: (id: number) => callbacksRef.current.onAbort?.(id),
        onBeforeSelect: (file: File, state: FileState[]) => callbacksRef.current.onBeforeSelect?.(file, state) ?? false,
        onSelect: (id: number, file: File, next: ProgressCallback, complete: CompleteCallback, error: ErrorCallback) =>
          callbacksRef.current.onSelect?.(id, file, next, complete, error),
        onStateChange: (state: FileState) => callbacksRef.current.onStateChange?.(state)
      });
      dragAndDropRef.current.init();
    }

    // Capture the current ref value
    const currentTimeouts = timeoutRefs.current;

    return () => {
      if (dragAndDropRef.current) {
        dragAndDropRef.current = null;
      }
      // Clear all pending timeouts
      currentTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      currentTimeouts.clear();
    };
  }, []); // Empty dependency array - only run once on mount

  // Update callback refs on every render (outside useEffect to avoid infinite loops)
  callbacksRef.current = {
    onAbort: onAbort,
    onBeforeSelect: onBeforeSelect,
    onSelect: onSelect,
    onStateChange: onStateChange
  };

  const downloadTemplate = () => {
    // Create Excel template with only headers (matching CreateUserDto columns)
    const headers = ['username', 'password', 'displayName', 'email', 'roles', 'authType'];

    // Create Excel file with only headers, no sample data
    const excelContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Users</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
        </head>
        <body>
          <table border="1">
            <thead>
              <tr>
                ${headers.map((header) => `<th style="background-color: #f0f0f0; font-weight: bold; padding: 8px; border: 1px solid #ccc;">${header}</th>`).join('')}
              </tr>
            </thead>
          </table>
        </body>
      </html>
    `;

    // Create and download Excel (.xls) file
    const excelBlob = new Blob([excelContent], {
      type: 'application/vnd.ms-excel'
    });
    const excelUrl = window.URL.createObjectURL(excelBlob);
    const excelLink = document.createElement('a');
    excelLink.href = excelUrl;
    excelLink.download = 'bulk_user_upload_template.xls';
    document.body.appendChild(excelLink);
    excelLink.click();
    document.body.removeChild(excelLink);
    window.URL.revokeObjectURL(excelUrl);
  };

  return (
    <div className={styles.section}>
      <div className={styles.settingsTitle}>Add Users in Bulk</div>
      <div className={styles.description}>
        Upload the Excel file using the provided template, then click Save to validate and confirm the user list.
      </div>
      <div className={styles.downloadSection}>
        <span className={styles.downloadLink} onClick={downloadTemplate}>
          <i className="icon icon-download-save" />
          Download Template
        </span>
      </div>

      {/* Upload File Section */}
      <div className={styles.container}>
        <div className={styles.containerHeader}>
          <div className={styles.containerTitle}>Upload file</div>
          <div className={styles.containerSubTitle}>
            Choose file to upload
            <ReactEDSWrapper
              component={Tooltip}
              message="Select an Excel file to upload. You can drag and drop or click to browse."
              pointer
            >
              <span className={styles.infoIcon}>
                <i className="icon icon-info" />
              </span>
            </ReactEDSWrapper>
          </div>
        </div>

        <div id="BulkUserUploadDragAndDrop" className="drag-and-drop">
          <form className="drag-and-drop-area">
            <i className="icon icon-upload"></i>
            <p>
              Drag & drop your file here or,{' '}
              <label htmlFor="bulk-user-upload" role="button" aria-label="Select files to upload">
                select to browse
                <input className="files" id="bulk-user-upload" type="file" accept=".xlsx,.xls" />
              </label>
            </p>
            <p>
              Supported format: Excel
              <br />
              Max: 20 files
            </p>
          </form>

          <div className={`header header-files ${styles.uploadedFilesTitle}`}>
            <div className="left">Uploaded files</div>
          </div>
          <div className="content content-files">
            <div className="file-pills"></div>
            <table className="upload-table"></table>
          </div>
        </div>
      </div>

      {/* Bulk Upload Results Section */}
      <div className={styles.bulkValidationSection}>
        <div className={styles.settingsTitle}>Bulk Email Validation</div>

        {uploadSuccess && <div className={styles.containerSubTitle}>{uploadSuccess}</div>}

        {uploadResults.length === 0 && uploadSuccess && (
          <div className={styles.noErrorsSection}>
            <i className="icon icon-info" style={{ marginRight: '8px' }} />
            <span>No errors detected</span>
          </div>
        )}

        {/* Upload Results Table - only show if there are errors */}
        {uploadResults.length > 0 && (
          <div className={styles.resultsTableSection}>
            <UploadResultsTable results={uploadResults} />
          </div>
        )}
      </div>
    </div>
  );
}

export default AddUsersBulk;
